/* ─── Australian State Location System ────────────────────────────────
 * Maps postcodes to states, provides state flags (colored badges),
 * and supports browser geolocation with override capability.
 * ──────────────────────────────────────────────────────────────────── */

export interface AuState {
  code: string;        // e.g. "NSW"
  name: string;        // e.g. "New South Wales"
  shortName: string;   // e.g. "NSW"
  color: string;         // hex colour for badge
  bgClass: string;       // Tailwind bg class
  textClass: string;     // Tailwind text class
  icon: string;          // Lucide icon name or emoji character
  postcodes: [number, number][]; // [min, max] ranges
}

export const AU_STATES: AuState[] = [
  {
    code: 'NSW', name: 'New South Wales', shortName: 'NSW',
    color: 'var(--rb-active)', bgClass: 'bg-rb-active', textClass: 'text-white',
    icon: '🔵',
    postcodes: [[2000, 2599], [2619, 2899], [2921, 2999]],
  },
  {
    code: 'VIC', name: 'Victoria', shortName: 'VIC',
    color: 'var(--rb-active)', bgClass: 'bg-rb-active', textClass: 'text-white',
    icon: '🔵',
    postcodes: [[3000, 3999], [8000, 8999]],
  },
  {
    code: 'QLD', name: 'Queensland', shortName: 'QLD',
    color: '#7f1d1d', bgClass: 'bg-[#7f1d1d]', textClass: 'text-white',
    icon: '🔴',
    postcodes: [[4000, 4999], [9000, 9999]],
  },
  {
    code: 'SA', name: 'South Australia', shortName: 'SA',
    color: '#be123c', bgClass: 'bg-[#be123c]', textClass: 'text-white',
    icon: '🔴',
    postcodes: [[5000, 5999]],
  },
  {
    code: 'WA', name: 'Western Australia', shortName: 'WA',
    color: '#ca8a04', bgClass: 'bg-[#ca8a04]', textClass: 'text-white',
    icon: '🟡',
    postcodes: [[6000, 6797], [6800, 6999]],
  },
  {
    code: 'TAS', name: 'Tasmania', shortName: 'TAS',
    color: '#166534', bgClass: 'bg-[#166534]', textClass: 'text-white',
    icon: '🟢',
    postcodes: [[7000, 7999]],
  },
  {
    code: 'NT', name: 'Northern Territory', shortName: 'NT',
    color: '#c2410c', bgClass: 'bg-[#c2410c]', textClass: 'text-white',
    icon: '🟠',
    postcodes: [[800, 999]],
  },
  {
    code: 'ACT', name: 'Australian Capital Territory', shortName: 'ACT',
    color: 'var(--rb-active)', bgClass: 'bg-rb-active', textClass: 'text-white',
    icon: '⚫',
    postcodes: [[2600, 2618], [2900, 2920]],
  },
];

/** Map a 4-digit postcode to its Australian state */
export function postcodeToState(postcode: string | number): AuState | null {
  const pc = typeof postcode === 'string' ? parseInt(postcode, 10) : postcode;
  if (isNaN(pc)) return null;
  for (const state of AU_STATES) {
    for (const [min, max] of state.postcodes) {
      if (pc >= min && pc <= max) return state;
    }
  }
  return null;
}

/** Guess state from a free-text location string (e.g. "Sydney NSW 2000") */
export function guessStateFromText(text: string): AuState | null {
  const upper = text.toUpperCase();
  // Direct state code match
  for (const state of AU_STATES) {
    if (upper.includes(state.code)) return state;
  }
  // Postcode extraction
  const matches = upper.match(/\b(\d{4})\b/);
  if (matches) {
    const st = postcodeToState(matches[1]);
    if (st) return st;
  }
  // City name hints
  if (/SYDNEY|NEWCASTLE|WOLLONGONG|ALBURY|DUBBO|TAMWORTH/.test(upper)) return AU_STATES[0]; // NSW
  if (/MELBOURNE|GEELONG|BALLARAT|BENDIGO|WARRNAMBOOL/.test(upper)) return AU_STATES[1]; // VIC
  if (/BRISBANE|GOLD COAST|CAIRNS|TOWNSVILLE|TOOWOOMBA/.test(upper)) return AU_STATES[2]; // QLD
  if (/ADELAIDE|GLENELG|PORT AUGUSTA|WHYALLA/.test(upper)) return AU_STATES[3]; // SA
  if (/PERTH|FREMANTLE|BROOME|KALGOORLIE|ALBANY/.test(upper)) return AU_STATES[4]; // WA
  if (/HOBART|LAUNCESTON|DEVONPORT|BURNIE/.test(upper)) return AU_STATES[5]; // TAS
  if (/DARWIN|ALICE SPRINGS|KATHERINE/.test(upper)) return AU_STATES[6]; // NT
  if (/CANBERRA|QUEANBEYAN/.test(upper)) return AU_STATES[7]; // ACT
  return null;
}

/* ─── UK EA / Devolved Agency Regions ──────────────────────────────────────── */

export interface UkEaRegion {
  name: string;        // e.g. "Thames"
  agency: string;      // e.g. "Environment Agency — Thames"
  color: string;
  bgClass: string;
  textClass: string;
  areaCodes: string[]; // postcode area codes (first 1–2 letters)
}

export const UK_EA_REGIONS: UkEaRegion[] = [
  {
    name: 'Thames',
    agency: 'Environment Agency — Thames',
    color: '#1d4ed8', bgClass: 'bg-[#1d4ed8]', textClass: 'text-white',
    areaCodes: ['SW','SE','EC','WC','W','E','N','NW','TW','KT','SM','CR','BR','DA','ME','GU','RG','HP','LU','SL','SG','AL','WD','EN','HA','UB','OX','MK'],
  },
  {
    name: 'Anglian',
    agency: 'Environment Agency — Anglian',
    color: '#0369a1', bgClass: 'bg-[#0369a1]', textClass: 'text-white',
    areaCodes: ['CO','IP','CB','PE','NR','IG','RM','SS','CM','NN'],
  },
  {
    name: 'Southern',
    agency: 'Environment Agency — Southern',
    color: '#0891b2', bgClass: 'bg-[#0891b2]', textClass: 'text-white',
    areaCodes: ['BN','PO','SO','RH','TN','CT'],
  },
  {
    name: 'South West',
    agency: 'Environment Agency — South West',
    color: '#0d9488', bgClass: 'bg-[#0d9488]', textClass: 'text-white',
    areaCodes: ['BS','BA','TA','SP','EX','TQ','PL','TR','GL','SN','DT'],
  },
  {
    name: 'Midlands',
    agency: 'Environment Agency — Midlands',
    color: '#7c3aed', bgClass: 'bg-[#7c3aed]', textClass: 'text-white',
    areaCodes: ['B','WV','WS','DY','DE','NG','LE','ST','TF','WR','CV','HR','LN'],
  },
  {
    name: 'North West',
    agency: 'Environment Agency — North West',
    color: '#c2410c', bgClass: 'bg-[#c2410c]', textClass: 'text-white',
    areaCodes: ['M','OL','BL','WN','WA','L','PR','BB','FY','CH','CW','LA','CA'],
  },
  {
    name: 'Yorkshire',
    agency: 'Environment Agency — Yorkshire',
    color: '#b45309', bgClass: 'bg-[#b45309]', textClass: 'text-white',
    areaCodes: ['LS','BD','HX','HD','HG','WF','DN','HU','YO','SK'],
  },
  {
    name: 'North East',
    agency: 'Environment Agency — North East',
    color: 'var(--rb-active)', bgClass: 'bg-rb-active', textClass: 'text-white',
    areaCodes: ['NE','SR','DH','TS','DL'],
  },
  {
    name: 'Wales (NRW)',
    agency: 'Natural Resources Wales',
    color: '#b91c1c', bgClass: 'bg-[#b91c1c]', textClass: 'text-white',
    areaCodes: ['CF','SA','LD','LL','NP','SY'],
  },
  {
    name: 'Scotland (SEPA)',
    agency: 'Scottish Environment Protection Agency',
    color: '#047857', bgClass: 'bg-[#047857]', textClass: 'text-white',
    areaCodes: ['EH','ML','TD','KY','KA','G','PA','FK','PH','DD','AB','IV','KW','ZE','HS','DG'],
  },
  {
    name: 'N. Ireland (NIEA)',
    agency: 'Northern Ireland Environment Agency',
    color: '#7c3aed', bgClass: 'bg-[#7c3aed]', textClass: 'text-white',
    areaCodes: ['BT'],
  },
];

/**
 * Map a UK postcode to its EA / devolved agency region.
 * Works with full postcodes (e.g. "SW1A 2AA"), partial (e.g. "SW1A") or just area codes (e.g. "SW").
 */
export function postcodeToUkRegion(postcode: string): UkEaRegion | null {
  const match = postcode.trim().toUpperCase().match(/^([A-Z]{1,2})/);
  if (!match) return null;
  const area = match[1];
  // Try exact area code match first (2-letter codes), then fall back to 1-letter
  for (const region of UK_EA_REGIONS) {
    if (region.areaCodes.includes(area)) return region;
  }
  // 2-letter area not found — try 1-letter prefix (e.g. area 'S' → Yorkshire)
  if (area.length === 2) {
    for (const region of UK_EA_REGIONS) {
      if (region.areaCodes.includes(area[0])) return region;
    }
  }
  return null;
}

/** UK region detection (enhanced with EA region + postcode support) */
export function guessUkRegion(text: string): UkEaRegion | null {
  const upper = text.toUpperCase();

  // 1. Try postcode pattern (e.g. "SW1A 2AA", "M1 1AE", "BT1 4GH")
  const postcodeMatch = upper.match(/\b([A-Z]{1,2}\d{1,2}[A-Z]?\s*\d[A-Z]{2})\b|\b([A-Z]{1,2}\d{1,2})\b/);
  if (postcodeMatch) {
    const pc = postcodeMatch[0];
    const region = postcodeToUkRegion(pc);
    if (region) return region;
  }

  // 2. City / place name hints
  if (/LONDON|THAMES|SURREY|KENT|HERTFORD|OXFORD|SLOUGH|READING|LUTON|BEDFORD/.test(upper)) return UK_EA_REGIONS[0]; // Thames
  if (/NORWICH|NORFOLK|SUFFOLK|CAMBRIDGE|IPSWICH|COLCHESTER|PETERBOROUGH|NORTHAMPTON/.test(upper)) return UK_EA_REGIONS[1]; // Anglian
  if (/BRIGHTON|PORTSMOUTH|SOUTHAMPTON|HOVE|EASTBOURNE|HASTINGS/.test(upper)) return UK_EA_REGIONS[2]; // Southern
  if (/BRISTOL|EXETER|PLYMOUTH|TRURO|BATH|SWINDON|GLOUCESTER|TAUNTON/.test(upper)) return UK_EA_REGIONS[3]; // South West
  if (/BIRMINGHAM|NOTTINGHAM|LEICESTER|COVENTRY|DERBY|STOKE|WOLVERHAMPTON|WORCESTER/.test(upper)) return UK_EA_REGIONS[4]; // Midlands
  if (/MANCHESTER|LIVERPOOL|CHESTER|LANCASTER|CARLISLE|BLACKPOOL|BOLTON|WIGAN/.test(upper)) return UK_EA_REGIONS[5]; // North West
  if (/LEEDS|SHEFFIELD|BRADFORD|HULL|YORK|HARROGATE|WAKEFIELD|DONCASTER/.test(upper)) return UK_EA_REGIONS[6]; // Yorkshire
  if (/NEWCASTLE|SUNDERLAND|DURHAM|TEESSIDE|MIDDLESBROUGH|GATESHEAD/.test(upper)) return UK_EA_REGIONS[7]; // North East
  if (/CARDIFF|SWANSEA|WALES|NEWPORT|ABERYSTWYTH/.test(upper)) return UK_EA_REGIONS[8]; // Wales
  if (/EDINBURGH|GLASGOW|ABERDEEN|DUNDEE|SCOTLAND|INVERNESS/.test(upper)) return UK_EA_REGIONS[9]; // Scotland
  if (/BELFAST|DERRY|NORTHERN IRELAND/.test(upper)) return UK_EA_REGIONS[10]; // N. Ireland

  // Default: Thames (most common)
  return UK_EA_REGIONS[0];
}

export interface SampleLocation {
  raw: string;           // e.g. "Sydney NSW 2000"
  state?: string;        // e.g. "NSW"
  stateName?: string;    // e.g. "New South Wales"
  country: string;       // e.g. "AU" or "UK"
  postcode?: string;     // e.g. "2000"
  latitude?: number;
  longitude?: number;
}

/** Default locations for demo data */
export const DEFAULT_AU_LOCATIONS: SampleLocation[] = [
  { raw: 'Sydney NSW 2000', state: 'NSW', stateName: 'New South Wales', country: 'AU', postcode: '2000' },
  { raw: 'Melbourne VIC 3000', state: 'VIC', stateName: 'Victoria', country: 'AU', postcode: '3000' },
  { raw: 'Brisbane QLD 4000', state: 'QLD', stateName: 'Queensland', country: 'AU', postcode: '4000' },
  { raw: 'Perth WA 6000', state: 'WA', stateName: 'Western Australia', country: 'AU', postcode: '6000' },
  { raw: 'Adelaide SA 5000', state: 'SA', stateName: 'South Australia', country: 'AU', postcode: '5000' },
];

export const DEFAULT_UK_LOCATIONS: SampleLocation[] = [
  { raw: 'Slough SL1 4DX', state: 'England', stateName: 'England', country: 'UK', postcode: 'SL1 4DX' },
  { raw: 'London SE16 4TN', state: 'England', stateName: 'England', country: 'UK', postcode: 'SE16 4TN' },
  { raw: 'Sheffield S9 2RL', state: 'England', stateName: 'England', country: 'UK', postcode: 'S9 2RL' },
  { raw: 'Bristol BS2 0UF', state: 'England', stateName: 'England', country: 'UK', postcode: 'BS2 0UF' },
  { raw: 'Aberdeen AB11 5RE', state: 'Scotland', stateName: 'Scotland', country: 'UK', postcode: 'AB11 5RE' },
];
